<template>
  <div>
    <Transition v-bind="fadeInTransition">
      <Card v-show="!editing" pt:content:class="p-0!" pt:body:class="p-2!">
        <template #content>
          <CondensedBudgetTable ref="condensedBudgetTable" v-model:project="project" v-model:draw-items="drawItems"></CondensedBudgetTable>
          <div class="flex justify-end">
            <Button
              icon="pi pi-cog"
              label="Customize Columns"
              severity="secondary"
              class="mx-3 text-surface-50! dark:text-surface-800!"
              @click="customizeBudgetColumns = true"
            ></Button>
            <Button v-if="canEdit" icon="pi pi-pencil" label="Edit" @click="openBudgetModal"></Button>
            <Button v-else icon="pi pi-eye" severity="info" label="View" @click="openBudgetModal"></Button>
          </div>
        </template>
      </Card>
    </Transition>

    <Card v-show="editing" pt:content:class="p-0! h-[87vh] flex flex-col" pt:body:class="py-2! px-0!">
      <template #content>
        <div v-show="editType === 'Budget'" class="h-full flex flex-col">
          <splitpanes horizontal class="default-theme pb-3">
            <pane class="bg-transparent!" :size="25">
              <div class="flex flex-col h-full mx-2">
                <SourceOfFundsTable
                  ref="sourceTable"
                  v-model:project-settings="project.projectSettings"
                  v-model:project-entities="sourceOfFunds"
                  v-model:drawItems="drawItems"
                  v-model:entity-retainage-list="entityRetainageList"
                  :entities
                  :draw-number="project.projectDrawInfo.drawNumber"
                  :editing
                  :draw-locked="project.projectDrawInfo.locked"
                  :project-info="project.projectInfo"
                  :contacts
                  :condensed="true"
                  @deleted-source="() => recalculateRetainage(false)"
                ></SourceOfFundsTable>
              </div>
            </pane>

            <pane class="bg-transparent!" :size="75">
              <div class="mx-2! h-full">
                <BudgetBuilder
                  ref="budgetBuilder"
                  v-show="editing"
                  v-model:draw-items="drawItems"
                  v-model:project-entities="sourceOfFunds"
                  v-model:project-settings="project.projectSettings"
                  v-model:project-info="project.projectInfo"
                  v-model:project-reminders="projectReminders"
                  v-model:entity-retainage-list="entityRetainageList"
                  :draw-number="project.projectDrawInfo.drawNumber"
                  :permissions="permissions"
                  :draw-locked="project.projectDrawInfo.locked"
                  :draw-id="project.projectDrawInfo.drawId"
                  :view-only="!canEdit"
                  @history-opened="displayHistory = true"
                  @history-closed="displayHistory = false"
                  @show-retainage="showRetainage"
                  size="h-full"
                ></BudgetBuilder>
              </div>
            </pane>
          </splitpanes>
          <div class="mx-2">
            <SubmitOptions v-show="!displayHistory && canEdit" @cancelled="budgetModalClosed" @submitted="submitBudgetChanges"></SubmitOptions>
            <div v-if="!canEdit" class="flex justify-between mt-3">
              <BackButton @click="budgetModalClosed"></BackButton>
            </div>
          </div>
        </div>

        <div v-show="editType === 'Retainage'" class="mx-2">
          <MaintainRetainage
            ref="maintainRetainage"
            v-model:project="project"
            v-model:source-of-funds="sourceOfFunds"
            v-model:entity-retainage-list="entityRetainageList"
            :get-retainage="false"
            :can-edit
            @submitted="submitRetainageChanges"
            @cancelled="retainageClosed"
          ></MaintainRetainage>
        </div>

        <TSDialog v-model:visible="showDrawBasedRetainage" label="Draw Based Retainage" icon="percentage" size="h-fit w-[900px]!">
          <template #content>
            <MaintainDrawBasedRetainage
              v-model:retainage="drawBasedRetainage"
              :project
              :can-edit
              :get-retainage="false"
              @cancelled="drawBasedRetainageClosed"
              @submitted="submitDrawBasedRetainage"
            ></MaintainDrawBasedRetainage>
          </template>
        </TSDialog>
      </template>
    </Card>
    <TSDialog label="Customize Budget Columns" v-model:visible="customizeBudgetColumns" v-if="customizeBudgetColumns" icon="cog" size="w-[900px]!">
      <template #content>
        <CustomizableColumnManager
          modal
          :initial-rows="
            project.projectSettings.condensedBudgetColumns == null
              ? getCondensedBudgetColumnDefault()
              : project.projectSettings.condensedBudgetColumns
          "
          :scope="SettingScope.BUDGET_COLUMN_TEMPLATES"
          @confirm="budgetColumnsCustomized"
        ></CustomizableColumnManager>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { DrawItemSchema, type DrawItem } from "@/types/DrawItem";
import { getDrawBasedRetainage, getRetainage } from "@/services/Utility";
import { ProjectSettingsSchema, type Project } from "@/types/Projects";
import { ProjectEntitySchema, type ProjectEntity } from "@/types/ProjectEntity";
import type { UserPermissions } from "@/types/User";
import type { ProjectReminder } from "@/types/Reminder";
import { fadeInTransition } from "@/services/Transitions";
import type { Entity } from "@/types/Entity";
import { Splitpanes, Pane } from "splitpanes";
import "splitpanes/dist/splitpanes.css";
import { DrawBasedRetainageSchema, EntityRetainageSchema, type DrawBasedRetainage, type EntityRetainage } from "@/types/Retainage";
import type { UseManualRefHistoryReturn } from "@vueuse/core";
import { getCondensedBudgetColumnDefault } from "@/enums/BudgetColumns";
import { usePropsStore } from "@/stores/props";
import { SettingScope } from "@/enums/SystemSettings";
import { useSystemStore } from "@/stores/systemStore";
import { z } from "zod";

const systemStore = useSystemStore();

const project = defineModel<Project>("project", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const sourceOfFunds = defineModel<ProjectEntity[]>("sourceOfFunds", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });
const projectReminders = defineModel<ProjectReminder[]>("projectReminders", { required: true });

const emit = defineEmits<{
  budgetUpdated: [];
}>();

interface Props {
  canEdit: boolean;
  permissions: UserPermissions;
  entities: Entity[];
  contacts: ProjectEntity[];
}
const props = defineProps<Props>();

const UpdateDrawSchema = z.object({
  projectSettings: ProjectSettingsSchema,
  items: z.array(DrawItemSchema),
  sourceOfFunds: z.array(ProjectEntitySchema),
  retainageList: z.array(EntityRetainageSchema),
  drawBasedRetainage: DrawBasedRetainageSchema,
});
type UpdateDraw = z.infer<typeof UpdateDrawSchema>;

const condensedBudgetTable = ref();
defineExpose({ condensedBudgetTable });

const editType = ref<"Budget" | "Retainage">("Budget");
const showDrawBasedRetainage = ref(false);
const entityRetainageList = ref<EntityRetainage[]>([]);
const drawBasedRetainage = ref<DrawBasedRetainage>(DrawBasedRetainageSchema.parse({}));

const budgetSubmitted = ref(false);
const retainageSubmitted = ref(false);
const displayHistory = ref(false);

const customizeBudgetColumns = ref(false);

const maintainRetainage = ref();

const sourceTable = ref();

let drawItemHistory = useManualRefHistory(drawItems, { clone: true });
let sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
let projectHistory = useManualRefHistory(project, { clone: true });
let retainageHistory: UseManualRefHistoryReturn<EntityRetainage[], EntityRetainage[]>;
let drawBasedRetainageHistory: UseManualRefHistoryReturn<DrawBasedRetainage, DrawBasedRetainage>;

const openBudgetModal = async (): Promise<void> => {
  drawItemHistory = useManualRefHistory(drawItems, { clone: true });
  sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
  projectHistory = useManualRefHistory(project, { clone: true });
  drawItemHistory.commit();
  sourceOfFundsHistory.commit();
  projectHistory.commit();
  budgetBuilder.value.rearrangeColumns();
  editing.value = true;
  editingTitle.value = "Editing Budget";
  await getRetainage(project.value.projectDrawInfo.drawId).then((retainage) => (entityRetainageList.value = retainage));
  await getDrawBasedRetainage(project.value.projectDrawInfo.drawId).then((retainage) => (drawBasedRetainage.value = retainage));
  setTimeout(() => sourceTable.value.toggleEditing(true), 10);
};

const budgetBuilder = ref();
const submitBudgetChanges = (): void => {
  systemStore.startLoading("Saving Budget...");
  drawItemHistory.commit();
  sourceOfFundsHistory.commit();
  projectHistory.commit();
  project.value.projectSettings.budgetColumnOrder = budgetBuilder.value.getBudgetColumnOrder();
  project.value.projectSettings.sourcesColumnOrder = sourceTable.value.getSourceColumnOrder();
  budgetSubmitted.value = true;
  const updateDraw: UpdateDraw = {
    projectSettings: project.value.projectSettings,
    items: drawItems.value,
    sourceOfFunds: sourceOfFunds.value,
    retainageList: entityRetainageList.value,
    drawBasedRetainage: drawBasedRetainage.value,
  };
  axios
    .post("/draw/update/budget/" + project.value.projectInfo.projectId + "/" + project.value.projectDrawInfo.drawNumber, updateDraw)
    .then(() => {
      emit("budgetUpdated");
      budgetModalClosed();
    })
    .catch((e) => errorApi(e))
    .finally(() => systemStore.stopLoading());
};

const budgetModalClosed = (): void => {
  if (!budgetSubmitted.value) {
    drawItemHistory.undo();
    sourceOfFundsHistory.undo();
    projectHistory.undo();

    //The undo function changes the format of the date to a string and messes up the display
    setTimeout(() => {
      project.value.projectDrawInfo.dateCreated = z.coerce.date().parse(project.value.projectDrawInfo.dateCreated);
      project.value.projectDrawInfo.completionDate = z.coerce.date().nullable().parse(project.value.projectDrawInfo.completionDate);
      project.value.projectDrawInfo.inspectionDate = z.coerce.date().nullable().parse(project.value.projectDrawInfo.inspectionDate);
    }, 10);
  }
  budgetSubmitted.value = false;
  editing.value = false;
  editingTitle.value = "";
};

const showRetainage = (): void => {
  if (drawItems.value.filter((item: DrawItem) => item.description === "DRAW BASED RETAINAGE").length > 0) {
    showDrawBasedRetainage.value = true;
    drawBasedRetainageHistory = useManualRefHistory(drawBasedRetainage, { clone: true });
    drawBasedRetainageHistory.commit();
  } else {
    maintainRetainage.value.updateTabs();
    editType.value = "Retainage";
    retainageHistory = useManualRefHistory(entityRetainageList, { clone: true });
    retainageHistory.commit();
  }
};

const submitRetainageChanges = (): void => {
  retainageHistory.commit();
  retainageSubmitted.value = true;

  recalculateRetainage(true);
};

const retainageClosed = (): void => {
  if (!retainageSubmitted.value) {
    retainageHistory.undo();
  }

  retainageSubmitted.value = false;
  editType.value = "Budget";
  nextTick(() => budgetBuilder.value.recalculateTotals());
};

const submitDrawBasedRetainage = (): void => {
  drawBasedRetainageHistory.commit();
  retainageSubmitted.value = true;

  recalculateRetainage(true);
};

const drawBasedRetainageClosed = (): void => {
  if (!retainageSubmitted.value) {
    drawBasedRetainageHistory.undo();
  }

  retainageSubmitted.value = false;
  showDrawBasedRetainage.value = false;
  nextTick(() => budgetBuilder.value.recalculateTotals());
};

const recalculateRetainage = (submitting: boolean) => {
  const updateDraw: UpdateDraw = {
    projectSettings: project.value.projectSettings,
    items: drawItems.value,
    sourceOfFunds: sourceOfFunds.value,
    retainageList: entityRetainageList.value,
    drawBasedRetainage: drawBasedRetainage.value,
  };

  let isDrawBased: boolean = drawItems.value.filter((item: DrawItem) => item.description === "DRAW BASED RETAINAGE").length > 0;
  const url = isDrawBased ? "/retainage/draw-based/recalculate" : "/retainage/recalculate";

  axios
    .post(url, updateDraw)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          const updateDraw = UpdateDrawSchema.parse(data);
          drawItems.value = updateDraw.items;
          sourceOfFunds.value = updateDraw.sourceOfFunds;
          project.value.projectSettings = updateDraw.projectSettings;
          if (submitting) {
            if (isDrawBased) {
              drawBasedRetainageClosed();
            } else {
              retainageClosed();
            }
          }
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const budgetColumnsCustomized = (columns: number) => {
  axios
    .post("/projects/condensed-budget-columns/" + project.value.projectInfo.projectId + "?cols=" + columns)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Budget Columns Updated", "");
          project.value.projectSettings.condensedBudgetColumns = columns;
          condensedBudgetTable.value.reloadTable();
          customizeBudgetColumns.value = false;
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

onMounted(async (): Promise<void> => {
  const propsStore = usePropsStore();
  if (propsStore.streamline) {
    await openBudgetModal();
  }
});
</script>
